import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ArrowRight,
} from "@phosphor-icons/react";
import { Badge } from "flowbite-react";
import { useAppleAdsBudgetHistory } from "../../../../hooks/get/useAppleAdsBudgetHistory";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import { Table } from "../../../../components/Table";
import { ColumnDef } from "@tanstack/react-table";
import { IAppleAdsBudgetHistory } from "../../interface/appleAds";
import { formatNumber } from "../../../../lib/utils";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { IDateRangePreset } from "../../../../interfaces";
import {
  CurrencyCode,
} from "../../../../shared/constants";
import { getCurrencySymbol } from "../../../../lib/utils";
import { useAppleAdsCampaignDetail } from "../../../../hooks/get/useAppleAdsCampaignDetail";
import { useIsMobile } from "../../../../hooks";
import BaseCampaignHistory from "../../components/shared/BaseCampaignHistory";

const AppleAdsHistory: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const isMobile = useIsMobile();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: campaignDetail } = useAppleAdsCampaignDetail({
    campaignId: campaign_id || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const campaign = campaignDetail?.campaign;
  const currency = (campaign?.currency as CurrencyCode) || "USD";

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const { data, isLoading } = useAppleAdsBudgetHistory({
    campaignId: campaign_id || "",
  });

  const filteredHistory = useMemo(() => {
    const dataToFilter = data?.history || [];
    if (!searchTerm) return dataToFilter;
    const lowerSearch = searchTerm.toLowerCase();
    return dataToFilter.filter(
      (item) =>
        item.changed_by.toLowerCase().includes(lowerSearch),
    );
  }, [data?.history, searchTerm]);

  const columns = useMemo<ColumnDef<IAppleAdsBudgetHistory>[]>(
    () => [
      {
        accessorKey: "changed_at",
        header: t("common.columns.dateTime", "Date & Time"),
        cell: ({ row }) => (
          <div className="text-sm text-gray-500 font-medium tabular-nums">
            {dayjs(row.original.timestamp).format("MMM DD, YYYY HH:mm")}
          </div>
        ),
      },
      {
        accessorKey: "change_type",
        header: t("common.columns.changeType", "Change Type"),
        cell: () => {
          return (
            <div className="flex items-center gap-2">
              <Badge
                color="success"
                size="xs"
                className="px-2 py-0.5 rounded-md font-medium uppercase tracking-wider"
              >
                BUDGET
              </Badge>
            </div>
          );
        },
      },
      {
        id: "changes",
        header: t("common.columns.changes", "Changes"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400 font-medium line-through decoration-gray-300 tabular-nums">
              {getCurrencySymbol(currency as CurrencyCode)}
              {formatNumber(parseFloat(row.original.old_daily_budget))}
            </span>
            <ArrowRight size={12} className="text-gray-400" weight="bold" />
            <span className="text-gray-800 font-bold tabular-nums">
              {getCurrencySymbol(currency as CurrencyCode)}
              {formatNumber(parseFloat(row.original.new_daily_budget))}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "remarks",
        header: t("common.columns.remarks", "Remarks"),
        cell: ({ row }) => (
          <div className="text-sm text-gray-500 italic">
            {row.original.changed_by || "-"}
          </div>
        ),
      },
    ],
    [t, currency, getCurrencySymbol],
  );

  return (
    <BaseCampaignHistory
      headerProps={{
        title: campaign?.apple_campaign_name || t("appleAds.campaignHistory", "Campaign History"),
        platform: "apple_ads",
        type: "history",
        appInfo: {
          id: campaign?.apple_campaign_id || campaign_id || "",
          name: campaign?.apple_campaign_name || "",
          logoUrl: campaign?.app_logo || null,
        },
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
        onSearch: setSearchTerm,
        searchPlaceholder: t("common.search", "Search logs..."),
      }}
    >
      <Table
        data={filteredHistory}
        columns={columns}
        isLoading={isLoading}
        loadingRowCount={10}
        isMobile={isMobile}
      />
    </BaseCampaignHistory>
  );
};

export default AppleAdsHistory;
